import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, interval, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

const REFRESH_KEY = 'refresh';
const DEFAULT_REFRESH_TIME = 3;
export const REFRESH_OPTIONS = [
  { label: '3 min', value: 3 },
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
];

export const minuteToMilliseconds = (minute: number) => minute * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class RefreshService implements OnDestroy {
  private intervalSubscription?: Subscription;
  private visibilitySubscription?: Subscription;
  private lastRefreshTime = Date.now();

  refreshTime$: BehaviorSubject<number> = new BehaviorSubject<number>(DEFAULT_REFRESH_TIME);
  readonly refresh$: Subject<void> = new Subject<void>();
  readonly selectedRefreshTime$ = this.refreshTime$.pipe(
    map((time) => {
      const refresh = REFRESH_OPTIONS.find((option) => option.value === time);
      if (refresh) {
        return refresh;
      }
      return REFRESH_OPTIONS[0];
    }),
  );

  constructor() {
    const refresh = Number(localStorage.getItem(REFRESH_KEY));
    if (!refresh && !REFRESH_OPTIONS.find((option) => option.value === refresh)) {
      this.setNewInterval(DEFAULT_REFRESH_TIME);
    } else {
      this.setNewInterval(refresh);
    }

    this.setupVisibilityChangeHandler();
  }

  ngOnDestroy() {
    this.intervalSubscription?.unsubscribe();
    this.visibilitySubscription?.unsubscribe();
  }

  setNewInterval(time: number) {
    localStorage.setItem(REFRESH_KEY, String(time));
    this.refreshTime$.next(time);
    this.intervalSubscription?.unsubscribe();
    this.intervalSubscription = interval(minuteToMilliseconds(time)).subscribe(() => {
      this.triggerRefresh();
    });
  }

  private triggerRefresh() {
    this.lastRefreshTime = Date.now();
    this.refresh$.next();
  }

  private setupVisibilityChangeHandler() {
    this.visibilitySubscription = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
    ).subscribe(() => {
      const currentTime = Date.now();
      const intervalMs = minuteToMilliseconds(this.refreshTime$.value);
      const timeSinceLastRefresh = currentTime - this.lastRefreshTime;

      if (timeSinceLastRefresh >= intervalMs) {
        this.triggerRefresh();
      }
    });
  }
}
